import clsx from 'clsx';
import React, { Fragment, useState } from 'react';
import ChevronLeftIcon from '../../assets/icons/chevron-down-icon.svg?react';
import EditIcon from '../../assets/icons/edit-icon.svg?react';
import TimeDurationIcon from '../../assets/icons/time-duration-icon.svg?react';
import GreenCheckIcon from '../../assets/icons/check-green-icon.svg?react';
import Button from '../../components/Button';
import WarningIcon from '../../assets/icons/warning-icon.svg?react';
import SidebarCrossIcon from '../../assets/icons/sidebar-cross-icon.svg?react';
import TrashIcon from '../../assets/icons/trash-icon.svg?react';
import { useButtonSizeLarge } from '../../utils/buttonSizes';
import ModalComponent from '../../components/ModalComponent';
import { useDispatch, useSelector } from 'react-redux';
import DueDateExtensionRequestModal from '../../components/DueDateExtensionRequestModal';
import ReportIcon from '../../assets/icons/report-icon.svg?react';
import { useFormik } from 'formik';
import {
  getApproveAndCloseSchema,
  getCorrectiveActionsDetailsSchema,
  getDueDateExtensionRequestSchema,
  getRejectActionSchema,
} from './schema';
import { toggleModal } from '../../reducers/modalToggle';
import ApproveAndCloseModal from '../../components/ApproveAndCloseModal';
import RejectActionModal from '../../components/RejectActionModal';
import {
  Attachments,
  CorrectiveActionsPriorityEnum,
  CorrectiveActionsStatusEnum,
  GetCorrectiveActionAssignedUsingIdResponse,
  LoginResponseData,
  RequestStatusEnum,
  User,
} from '@EHSNavigator/backend';
import moment from 'moment';
import ChangesHistory from '../../components/ChangesHistory';
import AssignedCorrectiveActionsDetailBanner from '../../components/AssignedCorrectiveActionsDetailBanner';
import RequestDueDateExtensionModal from '../../components/RequestDueDateExtensionModal';
import MarkAsReviewModal from '../../components/MarkAsReviewModal';
import { useLocalStorage } from '@uidotdev/usehooks';
import { toast } from 'react-toastify';
import { useApiClients } from '../../client/api/useApiClient';
import WithdrawRequestModal from '../../components/WithdrawRequestModal';
import { useMutation } from '@tanstack/react-query';
import AttachmentPreviewModal from '../../components/AttachmentPreviewModal';

interface AssignedCorrectiveActionsDetailProps {
  otherClasses?: string;
  handleSteps: React.Dispatch<number>;
  baseUrl: string;
  isAssigneToMe?: boolean;
  data: GetCorrectiveActionAssignedUsingIdResponse['correctiveAction'];
  refetch?: () => void;
}

const INITIAL_DUE_DATE_EXTENSION_REQUEST_STATE = {
  date: '',
  time: '',
};
const INITIAL_APPROVE_AND_CLOSE_STATE = {
  description: '',
};
const INITIAL_RREJECT_ACTION_STATE = {
  date: '',
  time: '',
  description: '',
};

const INITIAL_STATE = {
  description: '',
  files: [],
};
const AssignedCorrectiveActionsDetail: React.FC<
  AssignedCorrectiveActionsDetailProps
> = ({ otherClasses, data, handleSteps, baseUrl, isAssigneToMe, refetch }) => {
  const assignedCorrectiveActionsDetailClasses = clsx(otherClasses);
  const { CorrectiveActionApi } = useApiClients();
  const [selectedAttachment, setSelectedAttachment] =
    useState<Attachments | null>(null);
  const [userInfo] = useLocalStorage<LoginResponseData | null>(
    'userInfo',
    null,
  );
  const dispatch = useDispatch();
  const modalName = useSelector(
    (state: { modalToggle: { modalName: string } }) =>
      state.modalToggle.modalName,
  );

  const buttonSize = useButtonSizeLarge();

  const dueDateExtensionRequestHandler = useFormik({
    initialValues: INITIAL_DUE_DATE_EXTENSION_REQUEST_STATE,
    validationSchema: getDueDateExtensionRequestSchema(),
    onSubmit: async (values) => {
      try {
        const res =
          await CorrectiveActionApi.correctiveActionControllerRequestDateExtensionApprove(
            {
              requestId: data?.extensionRequestId as string,
              correctiveActionId: data?.id,
              approvedCorrectiveActionRequestDto: {
                date: moment(values?.date).format('DD/MM/YYYY'),
                time: moment(values?.time, 'HH:mm A').format('hh:mmA'),
              },
            },
          );

        refetch?.();
        toast?.success(res?.data?.message);
        dispatch(toggleModal(''));
        dueDateExtensionRequestHandler?.resetForm();
      } catch (error: any) {
        const errorMessage = error?.response?.data?.message;
        if (Array.isArray(errorMessage)) {
          errorMessage.forEach((msg: string) => toast.error(msg));
        } else {
          toast.error(errorMessage || 'An unexpected error occurred.');
        }
      }
    },
  });
  const approveAndCloseHandler = useFormik({
    initialValues: INITIAL_APPROVE_AND_CLOSE_STATE,
    validationSchema: getApproveAndCloseSchema(modalName),
    onSubmit: async (values) => {
      try {
        let res: any = {};
        if (modalName === 'approve-and-close-request-extension') {
          res =
            await CorrectiveActionApi.correctiveActionControllerRequestDateExtensionRejected(
              {
                requestId: data?.extensionRequestId as string,
                correctiveActionId: data?.id,
                rejectCorrectiveActionRequestDto: {
                  reason: values?.description,
                },
              },
            );
        } else if (modalName === 'request-review-approve') {
          res =
            await CorrectiveActionApi.correctiveActionControllerRequestReviewApprove(
              {
                requestId: data?.reviewRequestId as string,
                correctiveActionId: data?.id,
                approvedReviewCorrectiveActionRequestDto: {
                  reason: values.description || undefined,
                },
              },
            );
        } else if (modalName === 'request-review-reject') {
          res =
            await CorrectiveActionApi.correctiveActionControllerRequestReviewRejected(
              {
                requestId: data?.reviewRequestId as string,
                correctiveActionId: data?.id,
                rejectCorrectiveActionRequestDto: {
                  reason: values?.description,
                },
              },
            );
        }
        toast?.success(res?.data?.message);
        refetch?.();
        approveAndCloseHandler?.resetForm();
        dispatch(toggleModal(''));
      } catch (error: any) {
        const errorMessage = error?.response?.data?.message;
        if (Array.isArray(errorMessage)) {
          errorMessage.forEach((msg: string) => toast.error(msg));
        } else {
          toast.error(errorMessage || 'An unexpected error occurred.');
        }
      }
    },
  });
  const rejectActionHandler = useFormik({
    initialValues: INITIAL_RREJECT_ACTION_STATE,
    validationSchema: getRejectActionSchema(),
    onSubmit: async (values) => {
      console.log('Form submitted with values:', values);
      dispatch(toggleModal(''));
      rejectActionHandler?.resetForm();
    },
  });

  const extesionRequestHandler = useFormik({
    initialValues: INITIAL_STATE,
    validationSchema: getCorrectiveActionsDetailsSchema(),
    onSubmit: async (values) => {
      try {
        const res =
          await CorrectiveActionApi.correctiveActionControllerRequestDateExtension(
            {
              correctiveActionId: data?.id,
              description: values?.description,
              files: values?.files,
            },
          );

        refetch?.();
        toast?.success(res?.data?.message);
        dispatch(toggleModal(''));
        extesionRequestHandler?.resetForm();
      } catch (error: any) {
        const errorMessage = error?.response?.data?.message;
        if (Array.isArray(errorMessage)) {
          errorMessage.forEach((msg: string) => toast.error(msg));
        } else {
          toast.error(errorMessage || 'An unexpected error occurred.');
        }
      }
    },
  });
  const markAsReviewHandler = useFormik({
    initialValues: INITIAL_STATE,
    validationSchema: getCorrectiveActionsDetailsSchema(),
    onSubmit: async (values) => {
      try {
        const res =
          await CorrectiveActionApi.correctiveActionControllerRequestReview({
            correctiveActionId: data?.id,
            description: values?.description,
            files: values?.files,
          });

        refetch?.();
        toast?.success(res?.data?.message);
        dispatch(toggleModal(''));
        markAsReviewHandler?.resetForm();
      } catch (error: any) {
        const errorMessage = error?.response?.data?.message;
        if (Array.isArray(errorMessage)) {
          errorMessage.forEach((msg: string) => toast.error(msg));
        } else {
          toast.error(errorMessage || 'An unexpected error occurred.');
        }
      }
    },
  });

  const withdrawRequestMutation = useMutation({
    mutationFn: async ({
      correctiveActionId,
      requestId,
    }: {
      correctiveActionId: string;
      requestId: string;
    }) => {
      return await CorrectiveActionApi.correctiveActionControllerWithdrawRequestExtension(
        {
          correctiveActionId: correctiveActionId,
          requestId: requestId,
        },
      );
    },
    onSuccess: (data: any) => {
      refetch?.();
      toast.success(data?.data?.message);
      dispatch(toggleModal(''));
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message;
      if (Array.isArray(errorMessage)) {
        errorMessage.forEach((msg: string) => toast.error(msg));
      } else {
        toast.error(errorMessage || 'An unexpected error occurred.');
      }
    },
  });
  const pullBackReviewMutation = useMutation({
    mutationFn: async ({
      correctiveActionId,
      requestId,
    }: {
      correctiveActionId: string;
      requestId: string;
    }) => {
      return await CorrectiveActionApi.correctiveActionControllerRequestReviewPullback(
        {
          correctiveActionId: correctiveActionId,
          requestId: requestId,
        },
      );
    },
    onSuccess: (data: any) => {
      refetch?.();
      toast.success(data?.data?.message);
      dispatch(toggleModal(''));
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message;
      if (Array.isArray(errorMessage)) {
        errorMessage.forEach((msg: string) => toast.error(msg));
      } else {
        toast.error(errorMessage || 'An unexpected error occurred.');
      }
    },
  });

  const getPermission = () => {
    const currentUser = userInfo?.user as User;
    const role = currentUser?.role?.name as string;
    const isOwner = role === 'Owner';
    const isCreator = data?.user?.id === (currentUser?.id as string);
    const assigneeIds = data?.assignees?.map((node) => node?.userId) || [];
    const isAssignee = assigneeIds.includes(currentUser?.id as string);
    const isAdminOrManager = ['Admin', 'Manager'].includes(role);
    const isCreatorInAssignees = assigneeIds.includes(data?.user?.id as string);

    if (isOwner) {
      return true;
    }
    if (isCreator && !isAssignee) {
      return true;
    }
    if (isAdminOrManager && !isAssignee && isCreatorInAssignees) {
      return true;
    }
    return false;
  };
  const actions = getPermission();

  const getEditPermission = () => {
    const currentUser = userInfo?.user as User;
    const role = currentUser?.role?.name as string;
    const isOwner = role === 'Owner';
    const assigneeIds = data?.assignees?.map((node) => node?.userId) || [];
    const isAssignee = assigneeIds.includes(currentUser?.id as string);
    if (isOwner) {
      return false;
    }
    if (!isAssignee) {
      return false;
    }
    return true;
  };

  const editPermission = getEditPermission();
  console.log({ attach: data?.extensionRequest?.attachments });

  return (
    <div
      className={assignedCorrectiveActionsDetailClasses}
      data-testid="assigned-corrective-actions-detail"
    >
      <div>
        <Button
          href={`${baseUrl}`}
          variant="link"
          otherClasses="gap-2.5 w-fit mb-15"
        >
          <span className="bg-brand-byzantine rounded-5 flex h-8 w-8 items-center justify-center">
            <ChevronLeftIcon className="h-7 w-7 rotate-90 [&>path]:fill-white" />
          </span>
          <strong>Return</strong>
        </Button>
        <h1 className="h3 mb-1 capitalize lg:mb-0">{data?.title}</h1>
        <div className="mt-15 flex flex-wrap gap-x-2 gap-y-15 md:gap-x-15">
          <p className="text-neutral-tones-text/80 font-inter-variable -tracking-form-label lg:-tracking-button flex items-center gap-2.5 text-sm leading-5 lg:gap-15 lg:text-xl lg:leading-6">
            Status
            <span
              className={clsx(
                'font-bold uppercase',
                data?.status === CorrectiveActionsStatusEnum?.Active
                  ? 'text-brand-posh-pink'
                  : data?.status === CorrectiveActionsStatusEnum?.InReview
                    ? 'text-brand-fair-orange'
                    : data?.status === CorrectiveActionsStatusEnum?.Closed
                      ? 'text-brand-papery'
                      : 'text-brand-byzantine',
              )}
            >
              {data?.status}
            </span>
          </p>
          <p className="text-neutral-tones-text/80 font-inter-variable -tracking-form-label lg:-tracking-button border-l-neutral-tones-text/10 flex items-center gap-2 border-l pl-2 text-sm leading-5 md:gap-2.5 md:pl-15 lg:gap-15 lg:text-xl lg:leading-6">
            Reference ID
            <span className="text-neutral-tones-text/50 uppercase">
              {data?.correctActionRefId}
            </span>
          </p>
          <p className="text-neutral-tones-text/80 font-inter-variable -tracking-form-label lg:-tracking-button flex items-center gap-1 text-sm leading-5 md:gap-2.5 lg:gap-15 lg:text-xl lg:leading-6">
            Assigned by
            <span className="text-brand-byzantine font-bold capitalize">
              {data?.user?.fullName}
            </span>
          </p>
        </div>
      </div>

      {data?.status === CorrectiveActionsStatusEnum?.Closed ? (
        <></>
      ) : (
        data?.status === CorrectiveActionsStatusEnum?.InReview && (
          <>
            {actions && (
              <>
                <hr className="border-neutral-tones-text/10 mt-6 mb-5 lg:mt-15" />
                <div className="rounded-10 bg-brand-fair-orange mb-5 p-15">
                  <h6 className="font-inter-variable mb-15 flex items-center gap-15 text-lg font-bold text-white lg:text-xl">
                    <WarningIcon className="h-6 min-h-6 w-6 min-w-6 lg:h-8 lg:w-8 [&>g>path]:fill-current" />
                    Action Needed
                  </h6>
                  <p className="font-inter-variable text-form-label leading-5 font-normal text-white lg:text-base lg:font-bold">
                    You can choose to either close this action or reject the
                    submission, which will reopen this Corrective Action.
                  </p>
                </div>
              </>
            )}
          </>
        )
      )}

      <AssignedCorrectiveActionsDetailBanner
        data={data}
        otherClasses={clsx(
          '',
          data?.status === CorrectiveActionsStatusEnum?.InReview ||
            data?.status === CorrectiveActionsStatusEnum?.ExtensionRejected
            ? '!my-6 lg:!mb-9 lg:!mt-5'
            : '!my-6 lg:!my-9',
        )}
      />
      <div className="mt-6 py-6 lg:mt-6 lg:py-9">
        <div className="grid grid-cols-1 items-start justify-between gap-6 md:flex md:gap-10 lg:grid lg:grid-cols-3 lg:justify-start lg:gap-5 2xl:gap-10">
          <div className="flex flex-col">
            <h6 className="font-inter-variable text-brand-byzantine -tracking-button mb-5 text-lg leading-5 font-bold lg:text-xl lg:leading-6">
              Due Date & Time
            </h6>
            <div className="flex items-start gap-75 md:gap-12 lg:gap-6 2xl:gap-75">
              <div className="flex flex-col gap-2.5">
                <h6 className="text-neutral-tones-text -tracking-form-input lg:-tracking-button font-inter-variable text-base leading-6 font-bold lg:text-xl lg:leading-6">
                  Date
                </h6>
                <p className="font-inter-variable text-neutral-tones-text -tracking-form-input text-base leading-5 font-normal">
                  {moment(data?.dueDate).format('MMMM D, YYYY')}
                </p>
              </div>
              <div className="flex flex-col gap-2.5">
                <h6 className="text-neutral-tones-text -tracking-form-input lg:-tracking-button font-inter-variable text-base leading-6 font-bold lg:text-xl lg:leading-6">
                  Time
                </h6>
                <p className="font-inter-variable text-neutral-tones-text -tracking-form-input text-base leading-5 font-normal">
                  {moment(data?.dueTime, 'HH:mm:ss').format('h:mm a')}
                </p>
              </div>
            </div>
          </div>
          <div className="flex flex-col">
            <h6 className="font-inter-variable text-brand-byzantine -tracking-button mb-5 text-lg leading-5 font-bold lg:text-xl lg:leading-6">
              Assignee
            </h6>
            <div className="grid grid-cols-2 gap-6 lg:grid-cols-1 xl:grid-cols-2 2xl:gap-x-10">
              <div className="flex flex-col gap-2.5">
                <h6 className="text-neutral-tones-text -tracking-form-input lg:-tracking-button font-inter-variable text-base leading-6 font-bold lg:text-xl lg:leading-6">
                  Department
                </h6>
                <p className="font-inter-variable text-neutral-tones-text -tracking-form-input text-base leading-5 font-normal capitalize">
                  {data?.department?.name}
                </p>
              </div>
              <div className="flex flex-col gap-2.5">
                <h6 className="text-neutral-tones-text -tracking-form-input lg:-tracking-button font-inter-variable text-base leading-6 font-bold lg:text-xl lg:leading-6">
                  Assignee
                </h6>
                <p className="font-inter-variable text-neutral-tones-text -tracking-form-input text-base leading-5 font-normal capitalize">
                  {data?.assignees
                    ?.map((node) => node?.user?.fullName)
                    .join(', ')}
                </p>
              </div>
              {(data?.assignees || [])?.length > 1 && (
                <div className="flex flex-col gap-2.5">
                  <h6 className="text-neutral-tones-text -tracking-form-input lg:-tracking-button font-inter-variable text-base leading-6 font-bold lg:text-xl lg:leading-6">
                    Lead Assignee
                  </h6>
                  <p className="font-inter-variable text-neutral-tones-text -tracking-form-input text-base leading-5 font-normal capitalize">
                    {data?.leadAssignee?.user?.fullName}
                  </p>
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-col">
            <h6 className="font-inter-variable text-brand-byzantine -tracking-button mb-5 text-lg leading-5 font-bold lg:text-xl lg:leading-6">
              Priority
            </h6>
            <h3
              className={clsx(
                'uppercase',

                data?.priority === CorrectiveActionsPriorityEnum.Urgent
                  ? 'text-brand-posh-pink'
                  : data?.priority === CorrectiveActionsPriorityEnum.High
                    ? 'text-brand-fair-orange'
                    : data?.priority === CorrectiveActionsPriorityEnum.Medium
                      ? 'text-primary-yellow'
                      : 'text-brand-papery',
              )}
            >
              {data?.priority}
            </h3>
          </div>
        </div>
      </div>
      <div className="border-y-neutral-tones-text/10 w-full border-y py-6 lg:py-8">
        <div className="flex max-w-625 flex-col">
          <h6 className="font-inter-variable text-brand-byzantine -tracking-button mb-5 text-lg leading-5 font-bold lg:text-xl lg:leading-6">
            Action Description
          </h6>
          <p className="text-neutral-tones-text -tracking-form-input font-inter-variable text-base leading-5 font-normal capitalize italic">
            {data?.description}
          </p>
        </div>
      </div>
      {isAssigneToMe ? (
        <>
          {data?.status ===
          CorrectiveActionsStatusEnum.Closed ? null : data?.status ===
            CorrectiveActionsStatusEnum?.InReview ? (
            <div
              className={clsx(
                'my-9 flex flex-wrap items-center gap-x-15 gap-y-15',
              )}
            >
              {actions && (
                <>
                  <Button
                    otherClasses="bg-brand-papery hover:text-brand-papery hover:border-brand-papery "
                    size={buttonSize}
                    onClick={() =>
                      dispatch(toggleModal('request-review-approve'))
                    }
                  >
                    <GreenCheckIcon className="h-5 w-5 [&>path]:fill-current" />
                    Approve & Close
                  </Button>
                  <Button
                    variant="secondary"
                    otherClasses="text-brand-posh-pink hover:border-brand-posh-pink hover:text-brand-posh-pink"
                    size={buttonSize}
                    onClick={() =>
                      dispatch(toggleModal('request-review-reject'))
                    }
                  >
                    <SidebarCrossIcon className="h-5 w-5 [&>g>path]:fill-current" />
                    Reject Action
                  </Button>
                </>
              )}
              {data?.leadAssignee?.user?.id === userInfo?.user?.id && (
                <Button
                  otherClasses="bg-brand-posh-pink hover:text-brand-posh-pink hover:bg-transparent hover:border-brand-posh-pink"
                  size={buttonSize}
                  onClick={() => dispatch(toggleModal('review-pull-back'))}
                >
                  <ReportIcon className="h-5 w-5 [&>path]:fill-current" />
                  Pullback from Review
                </Button>
              )}
            </div>
          ) : (
            <>
              {data?.leadAssignee?.user?.id === userInfo?.user?.id && (
                <div
                  className={clsx(
                    'my-9 flex flex-wrap items-center gap-x-15 gap-y-15',
                  )}
                >
                  <Button
                    variant="secondary"
                    otherClasses="text-brand-fair-orange hover:border-brand-fair-orange hover:text-brand-fair-orange"
                    size={buttonSize}
                    onClick={() => dispatch(toggleModal('mark-for-review'))}
                  >
                    <GreenCheckIcon className="h-6 w-6 [&>path]:fill-current" />
                    Mark for Review
                  </Button>
                  <Button
                    size={buttonSize}
                    onClick={() =>
                      dispatch(toggleModal('request-due-date-extension'))
                    }
                    otherClasses=""
                    disable={Boolean(data?.extensionRequestId)}
                  >
                    <TimeDurationIcon className="h-6 w-6 [&>path]:fill-current" />
                    Request Extension
                  </Button>
                </div>
              )}
            </>
          )}
        </>
      ) : data?.status === CorrectiveActionsStatusEnum?.Active ? (
        <div
          className={clsx('my-9 flex flex-wrap items-center gap-x-15 gap-y-15')}
        >
          <Button
            disable={editPermission}
            size={buttonSize}
            otherClasses=""
            onClick={() => handleSteps(2)}
          >
            <EditIcon className="h-6 w-6 [&>path]:fill-current" />
            Edit Details
          </Button>
        </div>
      ) : (
        <>
          {data?.status === CorrectiveActionsStatusEnum.Closed
            ? null
            : data?.status === CorrectiveActionsStatusEnum?.InReview && (
                <div
                  className={clsx(
                    'my-9 flex flex-wrap items-center gap-x-15 gap-y-15',
                  )}
                >
                  {actions && (
                    <>
                      <Button
                        otherClasses="bg-brand-papery hover:text-brand-papery hover:border-brand-papery "
                        size={buttonSize}
                        onClick={() =>
                          dispatch(toggleModal('request-review-approve'))
                        }
                      >
                        <GreenCheckIcon className="h-5 w-5 [&>path]:fill-current" />
                        Approve & Close
                      </Button>
                      <Button
                        variant="secondary"
                        otherClasses="text-brand-posh-pink hover:border-brand-posh-pink hover:text-brand-posh-pink"
                        size={buttonSize}
                        onClick={() =>
                          dispatch(toggleModal('request-review-reject'))
                        }
                      >
                        <SidebarCrossIcon className="h-5 w-5 [&>g>path]:fill-current" />
                        Reject Action
                      </Button>
                    </>
                  )}
                  {data?.leadAssignee?.user?.id === userInfo?.user?.id && (
                    <Button
                      otherClasses="bg-brand-posh-pink hover:text-brand-posh-pink hover:bg-transparent hover:border-brand-posh-pink"
                      size={buttonSize}
                      onClick={() => dispatch(toggleModal('review-pull-back'))}
                    >
                      <ReportIcon className="h-5 w-5 [&>path]:fill-current" />
                      Pullback from Review
                    </Button>
                  )}
                </div>
              )}
        </>
      )}

      {data?.extensionRequestId && (
        <div className="border-y-neutral-tones-text/10 w-full border-y py-5">
          <div
            className={clsx(
              'bg-card-backgrounds/5 rounded-10 flex items-start gap-2.5 border p-15',
              data?.extensionRequest?.status === RequestStatusEnum.Approved
                ? 'border-brand-papery border-2'
                : data?.extensionRequest?.status === RequestStatusEnum.Rejected
                  ? 'border-brand-posh-pink border-2'
                  : 'border-neutral-tones-text/10',
            )}
          >
            <div className="flex-1">
              <div className="mb-6 flex w-full flex-col items-start justify-between gap-6 md:flex-row md:items-center md:gap-15 lg:mb-2.5 lg:gap-2.5">
                <TimeDurationIcon
                  className={clsx(
                    'hidden h-8 min-h-8 w-8 min-w-8 md:block 2xl:h-12 2xl:min-h-12 2xl:w-12 2xl:min-w-12',
                    data?.extensionRequest?.status ===
                      RequestStatusEnum.Approved
                      ? '[&>path]:fill-brand-papery'
                      : data?.extensionRequest?.status ===
                          RequestStatusEnum.Rejected
                        ? '[&>path]:fill-brand-posh-pink'
                        : '[&>path]:fill-brand-fair-orange',
                  )}
                />
                <h6
                  className={clsx(
                    'font-inter-variable -tracking-button flex flex-1 items-center gap-1 text-lg leading-5 font-bold md:justify-between lg:justify-start lg:text-xl lg:leading-6',
                    data?.extensionRequest?.status ===
                      RequestStatusEnum.Approved
                      ? 'text-brand-papery'
                      : data?.extensionRequest?.status ===
                          RequestStatusEnum.Rejected
                        ? 'text-brand-posh-pink'
                        : 'text-brand-fair-orange',
                  )}
                >
                  <TimeDurationIcon
                    className={clsx(
                      'h-12 min-h-12 w-12 min-w-12 md:hidden',
                      data?.extensionRequest?.status ===
                        RequestStatusEnum.Approved
                        ? '[&>path]:fill-brand-papery'
                        : data?.extensionRequest?.status ===
                            RequestStatusEnum.Rejected
                          ? '[&>path]:fill-brand-posh-pink'
                          : '[&>path]:fill-brand-fair-orange',
                    )}
                  />
                  Due Date Extension Request{' '}
                </h6>
                <h5
                  className={clsx(
                    'font-inter-variable text-base font-bold italic',
                    data?.extensionRequest?.status ===
                      RequestStatusEnum.Rejected
                      ? 'text-brand-posh-pink'
                      : data?.extensionRequest?.status ===
                          RequestStatusEnum.Approved
                        ? 'text-brand-papery'
                        : data?.extensionRequest?.status ===
                            RequestStatusEnum.Pending && 'text-primary-yellow',
                  )}
                >
                  {`${data?.extensionRequest?.status}`}
                  {data?.extensionRequest?.reviewer !== null && (
                    <>
                      {' '}
                      by{' '}
                      <span className="text-sm">
                        {`(${data?.extensionRequest?.reviewer?.fullName})`}
                      </span>
                    </>
                  )}
                </h5>
              </div>
              <div className="flex flex-col items-start gap-10">
                <div className="flex w-full max-w-804 flex-col gap-15 md:flex-row 2xl:ml-59">
                  <div className="w-full">
                    <h6 className="font-inter-variable text-neutral-tones-text -tracking-button mb-2.5 text-base leading-5 font-bold lg:text-xl lg:leading-6">
                      Reason for Extension
                    </h6>
                    <p className="font-inter-variable text-neutral-tones-text -tracking-form-input text-base leading-5 font-normal italic">
                      {data?.extensionRequest?.description}
                    </p>
                  </div>
                  <div className="w-44 min-w-44">
                    <h6 className="font-inter-variable text-neutral-tones-text -tracking-button mb-2.5 text-base leading-5 font-bold lg:text-xl lg:leading-6">
                      Images Uploaded
                    </h6>
                    <div className="mt-2.5 flex flex-col gap-2">
                      {(data?.extensionRequest?.attachments || []).length >
                      0 ? (
                        (data?.extensionRequest?.attachments || []).map(
                          (attachment) => {
                            const isPreviewable =
                              attachment?.mediaType?.startsWith('image/') ||
                              attachment?.mediaType?.startsWith('video/') ||
                              attachment?.mediaType === 'application/pdf' ||
                              attachment?.mediaType ===
                                'application/vnd.openxmlformats-officedocument.wordprocessingml.document'||
                                attachment?.mediaType === 'application/msword';
            
                                console.log({isPreviewable});
                                
                           
                                return (
                              <Fragment key={attachment?.id}>
                                {!isPreviewable ? (
                                  <a
                                    target="_blank"
                                    rel="noreferrer noreferrer"
                                    href={attachment?.url}
                                    className="font-inter-variable text-brand-byzantine -tracking-form-input text-base leading-5 font-normal break-words italic"
                                  >
                                    {attachment.name}
                                  </a>
                                ) : (
                                  <button
                                    onClick={() => {
                                      setSelectedAttachment(attachment);
                                      dispatch(
                                        toggleModal('attachment-preview-modal'),
                                      );
                                    }}
                                    className="font-inter-variable text-brand-byzantine -tracking-form-input text-left text-base leading-5 font-normal break-words italic"
                                  >
                                    {attachment.name}
                                  </button>
                                )}
                              </Fragment>
                            );
                          },
                        )
                      ) : (
                        <div className="font-inter-variable text-brand-byzantine -tracking-form-input mb-2.5 text-base leading-5 font-normal break-words italic">
                          No Attachments
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                {data?.extensionRequest?.status ===
                  RequestStatusEnum.Pending && (
                  <div className="flex flex-row flex-wrap gap-2.5">
                    {actions && (
                      <>
                        <Button
                          otherClasses="bg-brand-papery hover:text-brand-papery hover:border-brand-papery "
                          size="smaller"
                          onClick={() =>
                            dispatch(toggleModal('due-date-extension-request'))
                          }
                        >
                          <GreenCheckIcon className="h-5 w-5 [&>path]:fill-current" />
                          Approve
                        </Button>
                        <Button
                          otherClasses="bg-white !text-brand-posh-pink hover:text-brand-posh-pink hover:border-brand-posh-pink "
                          size="smaller"
                          onClick={() =>
                            dispatch(
                              toggleModal(
                                'approve-and-close-request-extension',
                              ),
                            )
                          }
                        >
                          <SidebarCrossIcon className="h-5 w-5 [&>g>path]:fill-current" />
                          Reject
                        </Button>
                      </>
                    )}
                    {data?.extensionRequest?.status ===
                      RequestStatusEnum.Pending &&
                      data?.leadAssignee?.user?.id === userInfo?.user?.id && (
                        <Button
                          otherClasses="bg-brand-posh-pink hover:text-brand-posh-pink hover:bg-transparent hover:border-brand-posh-pink "
                          size="smaller"
                          onClick={() =>
                            dispatch(toggleModal('withdraw-request'))
                          }
                        >
                          <TrashIcon className="h-5 w-5 [&>path]:stroke-current" />
                          Withdraw Request
                        </Button>
                      )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {data?.reviewRequestId && (
        <div className="flex w-full max-w-804 flex-col gap-15 pt-5 md:flex-row">
          <div className="w-full">
            <h6 className="font-inter-variable text-brand-byzantine -tracking-button mb-2.5 text-base leading-5 font-bold lg:text-xl lg:leading-6">
              Closing Notes by Assignee
            </h6>
            <p className="font-inter-variable text-neutral-tones-text -tracking-form-input text-base leading-5 font-normal italic">
              {data?.reviewRequest?.description}
            </p>
          </div>
          <div className="w-44 min-w-44">
            <h6 className="font-inter-variable text-neutral-tones-text -tracking-button mb-2.5 text-base leading-5 font-bold lg:text-xl lg:leading-6">
              Images Uploaded
            </h6>
            <div className="mt-2.5 flex flex-col gap-2">
              {(data?.reviewRequest?.attachments || []).length > 0 ? (
                (data?.reviewRequest?.attachments || []).map((attachment) => {
                  const isPreviewable =
                    attachment?.mediaType?.startsWith('image/') ||
                    attachment?.mediaType?.startsWith('video/') ||
                    attachment?.mediaType === 'application/pdf' ||
                    attachment?.mediaType === 'application/msword';

                    console.log({isPreviewable});
                    
                  return (
                    <Fragment key={attachment?.id}>
                      {!isPreviewable ? (
                        <a
                          target="_blank"
                          rel="noreferrer noreferrer"
                          key={attachment?.id}
                          className="font-inter-variable text-brand-byzantine -tracking-form-input text-base leading-5 font-normal break-words italic"
                        >
                          {attachment.name}
                        </a>
                      ) : (
                        <button
                          onClick={() => {
                            setSelectedAttachment(attachment);
                            dispatch(toggleModal('attachment-preview-modal'));
                          }}
                          className="font-inter-variable text-brand-byzantine -tracking-form-input text-left text-base leading-5 font-normal break-words italic"
                        >
                          {attachment.name}
                        </button>
                      )}
                    </Fragment>
                  );
                })
              ) : (
                <div className="font-inter-variable text-brand-byzantine -tracking-form-input mb-2.5 text-base leading-5 font-normal break-words italic">
                  No Attachments
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {data?.reviewRequest?.status !== RequestStatusEnum.InReview &&
        data?.reviewerComment && (
          <div className="flex w-full max-w-804 flex-col gap-15 pt-5 md:flex-row">
            <div className="w-full">
              <h6
                className={clsx(
                  'font-inter-variable -tracking-button mb-2.5 text-base leading-5 font-bold lg:text-xl lg:leading-6',
                  data?.reviewRequest?.status === RequestStatusEnum.Rejected
                    ? 'text-brand-posh-pink'
                    : 'text-brand-papery',
                )}
              >
                {data?.reviewRequest?.status === RequestStatusEnum.Rejected
                  ? `Rejection Notes by Assigner (${data?.reviewerComment?.user?.fullName})`
                  : `Approval Notes by Assigner (${data?.reviewerComment?.user?.fullName})`}
              </h6>
              <p className="font-inter-variable text-neutral-tones-text -tracking-form-input text-base leading-5 font-normal italic">
                {data?.reviewerComment?.comment}
              </p>
            </div>
          </div>
        )}

      {(data?.taskHistory || [])?.length > 0 && (
        <ChangesHistory data={data?.taskHistory} />
      )}

      {(modalName === 'due-date-extension-request' ||
        modalName === 'approve-and-close-request-extension' ||
        modalName === 'reject-action' ||
        modalName === 'request-due-date-extension' ||
        modalName === 'request-review-reject' ||
        modalName === 'review-pull-back' ||
        modalName === 'request-review-approve' ||
        modalName === 'withdraw-request' ||
        modalName === 'attachment-preview-modal' ||
        modalName === 'mark-for-review') && (
        <ModalComponent
          customClose={() => {
            dispatch(toggleModal(''));
            dueDateExtensionRequestHandler.resetForm();
            approveAndCloseHandler?.resetForm();
            rejectActionHandler?.resetForm();
            extesionRequestHandler?.resetForm();
            markAsReviewHandler?.resetForm();
            setSelectedAttachment(null);
          }}
          otherClasses="px-4 sm:px-0 py-4"
          additionalClass={clsx(
            'w-full rounded-2xl p-5 lg:px-9 lg:py-6 ',
            (modalName === 'due-date-extension-request' ||
              modalName === 'reject-action') &&
              'overflow-visible',
            modalName === 'attachment-preview-modal'
              ? 'w-full max-w-419 rounded-2xl !p-0' // max-w-766 !max-h-[850px] h-fit max-h-full !p-0 
              : 'max-w-573',
          )}
        >
          {modalName === 'due-date-extension-request' && (
            <DueDateExtensionRequestModal
              formHandler={dueDateExtensionRequestHandler}
              handleClose={() => {
                dispatch(toggleModal(''));
                dueDateExtensionRequestHandler.resetForm();
              }}
            />
          )}
          {modalName === 'request-review-approve' && (
            <ApproveAndCloseModal
              formHandler={approveAndCloseHandler}
              requestExtension="approve-review"
              handleClose={() => {
                dispatch(toggleModal(''));
                approveAndCloseHandler?.resetForm();
              }}
            />
          )}
          {modalName === 'approve-and-close-request-extension' && (
            <ApproveAndCloseModal
              formHandler={approveAndCloseHandler}
              handleClose={() => {
                dispatch(toggleModal(''));
                approveAndCloseHandler?.resetForm();
              }}
              requestExtension="request-extension"
            />
          )}
          {modalName === 'request-review-reject' && (
            <ApproveAndCloseModal
              formHandler={approveAndCloseHandler}
              handleClose={() => {
                dispatch(toggleModal(''));
                approveAndCloseHandler?.resetForm();
              }}
              requestExtension="review-reject"
            />
          )}
          {modalName === 'reject-action' && (
            <RejectActionModal
              formHandler={rejectActionHandler}
              handleClose={() => {
                dispatch(toggleModal(''));
                rejectActionHandler?.resetForm();
              }}
            />
          )}
          {modalName === 'request-due-date-extension' && (
            <RequestDueDateExtensionModal
              formHandler={extesionRequestHandler}
            />
          )}
          {modalName === 'mark-for-review' && (
            <MarkAsReviewModal
              formHandler={markAsReviewHandler}
              handleClose={() => {
                dispatch(toggleModal(''));
                markAsReviewHandler?.resetForm();
              }}
            />
          )}
          {modalName === 'withdraw-request' && (
            <WithdrawRequestModal
              disbale={withdrawRequestMutation?.isPending}
              handleClose={() => dispatch(toggleModal(''))}
              handleMutation={() =>
                withdrawRequestMutation?.mutate({
                  correctiveActionId: data?.id,
                  requestId: data?.extensionRequest?.id as string,
                })
              }
            />
          )}
          {modalName === 'review-pull-back' && (
            <WithdrawRequestModal
              disbale={pullBackReviewMutation?.isPending}
              handleClose={() => dispatch(toggleModal(''))}
              isReviewPullBack
              handleMutation={() =>
                pullBackReviewMutation?.mutate({
                  correctiveActionId: data?.id,
                  requestId: data?.reviewRequestId as string,
                })
              }
            />
          )}
          {modalName === 'attachment-preview-modal' && (
            <AttachmentPreviewModal
              handleClose={() => {
                dispatch(toggleModal(''));
                setSelectedAttachment(null);
              }}
              attachment={selectedAttachment}
            />
          )}
        </ModalComponent>
      )}
    </div>
  );
};

export default AssignedCorrectiveActionsDetail;
